/**
 * FoundationLessonPage – immersive page for a single sound lesson.
 *
 * Sections:
 *   1. Header + mouth-position SVG
 *   2. Listen (example) — slow / normal
 *   3. Discrimination drill ("Which did I say?") — up to 4 pairs
 *   4. Production drill (record each target word, score via Whisper)
 *   5. "Mark as learned" CTA (server auto-completes when thresholds met)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CaretLeft,
  CheckCircle,
  Circle,
  SpeakerHigh,
  Microphone,
  Stop,
  ArrowsCounterClockwise,
  SpinnerGap,
  Ear,
  Lightning,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';
import useRecorder from '@/hooks/useRecorder';
import { renderSafeSvg } from '@/lib/safeSvg';

export default function FoundationLessonPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState('');
  const [speaking, setSpeaking] = useState(null); // text currently playing
  const [completing, setCompleting] = useState(false);
  // Discrimination drill state
  const [drillIdx, setDrillIdx] = useState(0); // which pair
  const [drillActual, setDrillActual] = useState(null); // 'a' | 'b' — what was played
  const [drillChoice, setDrillChoice] = useState(null); // learner's pick
  const [drillCounts, setDrillCounts] = useState({ correct: 0, attempts: 0 });
  // Production drill state — per target word
  const [selectedWord, setSelectedWord] = useState('');
  const [scoreByWord, setScoreByWord] = useState({}); // word → {score, passed}
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const recorder = useRecorder();
  const audioElRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/foundations/lessons/${id}`);
        setLesson(data);
        setDrillCounts({
          correct: data.progress?.discrim_correct || 0,
          attempts: data.progress?.discrim_attempts || 0,
        });
        // Preload scoreByWord from words_passed — we only store "passed" flag.
        const init = {};
        (data.progress?.words_passed || []).forEach((w) => {
          init[w] = { passed: true, score: 1.0 };
        });
        setScoreByWord(init);
        setSelectedWord((data.target_words || [])[0] || '');
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, [id]);

  // ----- speak helper -----
  async function speak(text, slow = false) {
    if (!text) return;
    setSpeaking(text);
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text, speed: slow ? 0.7 : 0.95 }),
      });
      if (!res.ok) throw new Error('tts failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioElRef.current = audio;
      await new Promise((resolve) => {
        audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        audio.play().catch(() => resolve());
      });
    } catch { /* ignore */ } finally {
      setSpeaking(null);
    }
  }

  // ----- discrimination drill -----
  const currentPair = lesson?.minimal_pairs?.[drillIdx] || null;
  async function playDrillPair() {
    if (!currentPair) return;
    // Pick A or B at random and play it. Record the actual.
    const pick = Math.random() < 0.5 ? 'a' : 'b';
    setDrillActual(pick);
    setDrillChoice(null);
    await speak(currentPair[pick], false);
  }
  async function chooseDrill(choice) {
    if (!drillActual || drillChoice) return;
    setDrillChoice(choice);
    const correct = choice === drillActual;
    // Optimistic UI
    setDrillCounts((c) => ({
      correct: c.correct + (correct ? 1 : 0),
      attempts: c.attempts + 1,
    }));
    try {
      await http.post('/foundations/discriminate', {
        lesson_id: id,
        pair_index: drillIdx,
        correct,
      });
    } catch { /* non-fatal */ }
    // Advance after a moment
    setTimeout(() => {
      if (drillIdx + 1 < (lesson?.minimal_pairs?.length || 0)) {
        setDrillIdx((i) => i + 1);
        setDrillActual(null);
        setDrillChoice(null);
      } else {
        // Stay — learner can replay drills
        setDrillActual(null);
        setDrillChoice(null);
      }
    }, 900);
  }
  function resetDrill() {
    setDrillIdx(0);
    setDrillActual(null);
    setDrillChoice(null);
  }

  // ----- production drill -----
  async function startRec(word) {
    setSelectedWord(word);
    try {
      await recorder.start();
      setRecording(true);
    } catch (e) {
      setError(e?.message || 'Microphone permission denied');
    }
  }
  async function stopRec() {
    try {
      const blob = await recorder.stop();
      setRecording(false);
      if (!blob) return;
      setSubmitting(true);
      const fd = new FormData();
      fd.append('audio', blob, 'foundation.webm');
      const token = getStoredToken();
      const res = await fetch(`${API}/transcribe`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const j = await res.json();
      const heard = (j?.text || '').trim();
      const { data } = await http.post('/foundations/attempt', {
        lesson_id: id,
        target: selectedWord,
        heard,
      });
      setScoreByWord((s) => ({
        ...s,
        [selectedWord]: {
          score: data.score,
          passed: data.passed,
          heard,
        },
      }));
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function complete() {
    setCompleting(true);
    try {
      await http.post('/foundations/complete', { lesson_id: id });
      navigate('/foundations');
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setCompleting(false);
    }
  }

  const passedCount = useMemo(
    () => Object.values(scoreByWord).filter((v) => v?.passed).length,
    [scoreByWord],
  );

  if (!lesson) {
    return (
      <div className="lf-shell flex flex-col px-6 py-8">
        <BackBar />
        <div className="mt-8 flex items-center gap-2 text-sm text-[var(--lf-text-muted)]">
          {error ? (
            <span className="text-[var(--lf-destructive)]">{error}</span>
          ) : (
            <><SpinnerGap className="animate-spin" size={18} /> Loading lesson…</>
          )}
        </div>
      </div>
    );
  }

  const totalPairs = lesson.minimal_pairs?.length || 0;

  return (
    <div className="lf-shell flex flex-col px-6 py-8 pb-28" data-testid="foundation-lesson-page">
      <BackBar />

      {/* Header */}
      <div className="mt-4 flex items-start gap-4">
        <div
          className="grid h-24 w-24 flex-shrink-0 place-items-center rounded-2xl bg-[var(--lf-surface)] text-[var(--lf-primary)] border border-[var(--lf-border)]"
          aria-hidden
          {...renderSafeSvg(lesson.mouth_svg)}
          data-testid="foundation-mouth-svg"
        />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Lesson {lesson.order} · {lesson.ipa}
          </div>
          <h1 className="text-2xl font-bold">{lesson.title}</h1>
          <div className="mt-1 text-sm text-[var(--lf-text-muted)]">{lesson.summary}</div>
        </div>
      </div>

      {/* Mouth hint */}
      <div className="mt-5 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-4 text-sm" data-testid="foundation-mouth-hint">
        <div className="font-semibold text-[var(--lf-primary)]">Mouth position</div>
        <div className="mt-1">{lesson.mouth_hint}</div>
      </div>

      {/* 1. Listen */}
      <section className="mt-6" data-testid="foundation-listen-section">
        <SectionTitle icon={<Ear size={16} weight="fill" />}>Listen</SectionTitle>
        <div className="lf-card mt-2 p-4">
          <div className="text-base italic">"{lesson.example}"</div>
          <div className="text-sm text-[var(--lf-text-muted)]">{lesson.example_native}</div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => speak(lesson.example, false)}
              disabled={!!speaking}
              className="lf-btn lf-btn-secondary flex-1"
              data-testid="foundation-listen-normal"
            >
              {speaking === lesson.example ? (
                <SpinnerGap size={14} className="animate-spin" />
              ) : (
                <SpeakerHigh size={14} weight="fill" />
              )}{' '}
              Normal
            </button>
            <button
              onClick={() => speak(lesson.example, true)}
              disabled={!!speaking}
              className="lf-btn lf-btn-ghost flex-1"
              data-testid="foundation-listen-slow"
            >
              Slow
            </button>
          </div>
        </div>
      </section>

      {/* 2. Discrimination drill */}
      <section className="mt-6" data-testid="foundation-discrim-section">
        <SectionTitle icon={<Ear size={16} weight="fill" />}>
          Ear training · {drillCounts.correct} / {drillCounts.attempts || totalPairs}
        </SectionTitle>
        <div className="lf-card mt-2 p-4">
          <div className="text-sm text-[var(--lf-text-muted)]">
            Pair {Math.min(drillIdx + 1, totalPairs)} of {totalPairs}. Tap ▶️ to hear one word, then pick which one you heard.
          </div>

          {currentPair ? (
            <>
              <button
                onClick={playDrillPair}
                disabled={!!speaking}
                className="lf-btn lf-btn-primary mt-3 w-full py-3"
                data-testid="foundation-drill-play"
              >
                {speaking ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <SpeakerHigh size={16} weight="fill" />
                )}
                Play a word
              </button>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {['a', 'b'].map((key) => {
                  const word = currentPair[key];
                  const revealed = !!drillChoice;
                  const ok = revealed && key === drillActual;
                  const no = revealed && drillChoice === key && key !== drillActual;
                  return (
                    <button
                      key={key}
                      onClick={() => chooseDrill(key)}
                      disabled={!drillActual || !!drillChoice}
                      className={`lf-card p-3 text-left transition-all ${
                        ok ? 'ring-2 ring-[var(--lf-primary)]' : ''
                      } ${no ? 'opacity-50' : ''}`}
                      data-testid={`foundation-drill-pick-${key}`}
                    >
                      <div className="text-base font-semibold">{word}</div>
                      <div className="text-xs text-[var(--lf-text-muted)]">
                        {key === 'a'
                          ? currentPair.translation_a
                          : currentPair.translation_b}
                      </div>
                      {ok ? (
                        <CheckCircle size={16} weight="fill" className="mt-1 text-[var(--lf-primary)]" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="mt-3 text-sm">All pairs done for now.</div>
          )}

          <button
            onClick={resetDrill}
            className="lf-btn lf-btn-ghost mt-3 w-full text-sm"
            data-testid="foundation-drill-reset"
          >
            <ArrowsCounterClockwise size={14} /> Reset drill
          </button>
        </div>
      </section>

      {/* 3. Production */}
      <section className="mt-6" data-testid="foundation-production-section">
        <SectionTitle icon={<Lightning size={16} weight="fill" />}>
          Your turn · {passedCount} / {lesson.target_words.length}
        </SectionTitle>
        <div className="lf-card mt-2 p-4">
          <div className="text-sm text-[var(--lf-text-muted)]">
            Tap a word to focus, then press and say it. Green means it passed.
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {lesson.target_words.map((w) => {
              const r = scoreByWord[w];
              return (
                <button
                  key={w}
                  onClick={() => setSelectedWord(w)}
                  className={`rounded-full px-3 py-2 text-sm font-semibold border transition-all ${
                    selectedWord === w
                      ? 'border-[var(--lf-primary)] ring-2 ring-[var(--lf-primary)]'
                      : 'border-[var(--lf-border)]'
                  } ${
                    r?.passed
                      ? 'bg-[var(--lf-primary)] text-white'
                      : 'bg-[var(--lf-surface)]'
                  }`}
                  data-testid={`foundation-word-${w}`}
                >
                  {r?.passed ? <CheckCircle size={12} weight="fill" className="inline mr-1" /> : null}
                  {w}
                </button>
              );
            })}
          </div>

          {selectedWord ? (
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => speak(selectedWord, true)}
                disabled={!!speaking}
                className="lf-btn lf-btn-ghost py-2"
                data-testid="foundation-word-listen"
              >
                <SpeakerHigh size={14} weight="fill" /> Listen
              </button>
              {!recording ? (
                <button
                  onClick={() => startRec(selectedWord)}
                  disabled={submitting}
                  className="lf-btn lf-btn-primary flex-1 py-3"
                  data-testid="foundation-rec-start"
                >
                  <Microphone size={16} weight="fill" /> Say "{selectedWord}"
                </button>
              ) : (
                <button
                  onClick={stopRec}
                  className="lf-btn lf-btn-destructive flex-1 py-3"
                  data-testid="foundation-rec-stop"
                >
                  <Stop size={16} weight="fill" /> Stop
                </button>
              )}
            </div>
          ) : null}

          {scoreByWord[selectedWord] ? (
            <AttemptBanner pronScore={scoreByWord[selectedWord]} target={selectedWord} />
          ) : null}
        </div>
      </section>

      {error ? (
        <div className="mt-4 text-sm text-[var(--lf-destructive)]">{error}</div>
      ) : null}

      {/* Sticky footer CTA */}
      <div className="fixed inset-x-0 bottom-0 border-t border-[var(--lf-border)] bg-[var(--lf-surface)] px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-screen-sm items-center gap-3">
          <button
            onClick={complete}
            disabled={completing}
            className="lf-btn lf-btn-primary flex-1 py-3"
            data-testid="foundation-complete-cta"
          >
            {completing ? (
              <SpinnerGap size={16} className="animate-spin" />
            ) : (
              <>
                Mark as learned <CheckCircle size={16} weight="fill" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function BackBar() {
  return (
    <div className="flex items-center gap-2">
      <Link
        to="/foundations"
        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
        data-testid="foundation-back"
      >
        <CaretLeft size={18} />
      </Link>
      <h1 className="text-2xl font-bold">Sound lesson</h1>
    </div>
  );
}

function SectionTitle({ children, icon }) {
  return (
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
      {icon}
      {children}
    </div>
  );
}

function AttemptBanner({ pronScore, target }) {
  const ok = pronScore.score >= 0.6;
  return (
    <div
      className={`mt-3 rounded-2xl border p-3 text-sm ${
        ok
          ? 'border-[var(--lf-primary)] bg-[#FFF5EE] text-[var(--lf-primary)]'
          : 'border-[var(--lf-border)] bg-[var(--lf-surface-muted)] text-[var(--lf-text)]'
      }`}
      data-testid="foundation-score-banner"
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold">
          {ok ? <CheckCircle size={16} weight="fill" className="inline mr-1" /> : <Circle size={16} className="inline mr-1" />}
          {Math.round(pronScore.score * 100)}% · target: {target}
        </div>
      </div>
      {pronScore.heard ? (
        <div className="mt-1 text-xs italic opacity-80">heard: "{pronScore.heard}"</div>
      ) : null}
    </div>
  );
}
